import { Component, Element, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { debounce } from "../../utils/debounce";
import { getViewportWidth } from "../../utils/getViewportWidth";

/**
 * Accordion component that can expand or collapse to show or hide content.
 *
 * @slot (default) - The content of the accordion
 * @slot title - The element for the accordion title
 */
@Component({
    tag: "ux-accordion",
    styleUrl: "accordion.scss",
    shadow: false
})
export class Accordion {
    #baseClass = "ux-accordion";
    #contentContainer: HTMLDivElement;
    #titleContainer: HTMLButtonElement;
    #title: Element;
    #slotObserver: MutationObserver;
    #tabObserver: MutationObserver;
    #slotElement: HTMLSlotElement;

    @Element() element!: HTMLUxAccordionElement;

    /**
     * The amount in pixels from which the accordion functionality is deactivated.
     * When the viewport width is greater than or equal to this value, the accordion will not function.
     * @deprecated type will be changed to number in the next major version
     */
    @Prop()
    readonly noAccordionFrom: number | null = null;

    /**
     * The class to be passed to the title of the accordion when it becomes active.
     * This class is added to the title element when the accordion is expanded.
     */
    @Prop()
    readonly activeTitleClass: string = "ux-text-link--green";

    /**
     * If a click event is detected on this selector, the accordion will not trigger.
     * This is useful for excluding certain elements from triggering the accordion.
     */
    @Prop()
    readonly noClickSelector: string = "";

    /**
     * Determines if the accordion is opened on initial view.
     * This property can be mutated and reflected.
     */
    @Prop({
        mutable: true,
        reflect: true
    })
    isExpanded = false;

    @State() noAccordion = this.noAccordionFrom !== null ? window.innerWidth >= this.noAccordionFrom : false;
    @State() windowWidth: number = getViewportWidth();

    connectedCallback(): void {
        window.addEventListener("resize", this.#onResize);
    }

    componentDidLoad(): void {
        if (!this.#titleContainer.firstElementChild) {
            return;
        }
        this.#title = this.#titleContainer.firstElementChild;
        this.#slotElement.addEventListener("slotchange", () => {
            if (!this.#titleContainer.firstElementChild) {
                return;
            }
            this.#title = this.#titleContainer.firstElementChild;
        });

        this.setIsExpanded();
        this.#slotObserver = new MutationObserver(() => this.#handleSlotChange());
        this.#slotObserver.observe(this.#contentContainer, { childList: true, subtree: true });
        const tabElement = this.element.closest("ux-tab");
        if (tabElement) {
            this.#tabObserver = new MutationObserver(() => {
                if (tabElement.classList.contains("is-active")) {
                    requestAnimationFrame(() => this.setIsExpanded());
                }
            });
            this.#tabObserver.observe(tabElement, { attributes: true, attributeFilter: ["class"] });
        }
    }

    #onResize = debounce((): void => {
        this.windowWidth = getViewportWidth();
        this.setIsExpanded();
        if (!this.noAccordionFrom) {
            return;
        }
        this.noAccordion = this.windowWidth >= this.noAccordionFrom;
        if (this.noAccordion) {
            this.#title?.classList.remove(this.activeTitleClass);
        }
    }, 200);

    #matchingElement = (selector: string, clickedElement: HTMLElement): boolean => {
        return clickedElement.closest(selector) !== null;
    };

    @Watch("isExpanded")
    setIsExpanded(): void {
        this.#setOrRemoveTitleClass();

        if (this.isExpanded) {
            this.#contentContainer.style.maxHeight =
                this.#contentContainer.scrollHeight + (this.windowWidth > 767 ? 65 : 50) + "px";
        } else {
            this.#contentContainer.style.maxHeight = "";
        }
    }

    @Listen("uxCollapsibleExpanded", { target: "window" })
    handleCollapsibleExpansion(): void {
        this.#onResize();
    }

    #handleSlotChange = (): void => {
        this.#onResize();
    };

    #setOrRemoveTitleClass(): void {
        if (this.activeTitleClass !== "" && this.#title !== null) {
            if (this.isExpanded) {
                this.#title.classList.add(this.activeTitleClass);
            } else {
                this.#title.classList.remove(this.activeTitleClass);
            }
        }
    }

    #handleAccordionClick = (e: Event): void => {
        const clickTarget = e.target as HTMLElement;

        if (this.noClickSelector !== "") {
            if (this.#matchingElement(this.noClickSelector, clickTarget)) {
                return;
            }
        }

        if (this.noAccordion) {
            return;
        }

        this.isExpanded = !this.isExpanded;
    };

    disconnectedCallback(): void {
        this.#slotObserver.disconnect();
        this.#tabObserver.disconnect();
        window.removeEventListener("resize", this.#onResize);
        this.#slotElement.removeEventListener("slotchange", () => this.#handleSlotChange());
    }

    render() {
        return (
            <Host
                class={{
                    [this.#baseClass]: true,
                    [`${this.#baseClass}--active`]: this.isExpanded && !this.noAccordion
                }}
            >
                <button
                    class={{
                        [`${this.#baseClass}__button`]: true,
                        [`${this.#baseClass}__title-container`]: !this.noAccordion
                    }}
                    onClick={(e) => this.#handleAccordionClick(e)}
                    ref={(el) => (this.#titleContainer = el as HTMLButtonElement)}
                    aria-expanded={this.isExpanded ? "true" : "false"}
                    type="button"
                >
                    <slot
                        name="title"
                        ref={(el) => {
                            this.#slotElement = el as HTMLSlotElement;
                        }}
                    />
                    {!this.noAccordion && <ux-icon name="chevron-down" weight="bold" aria-hidden="true"></ux-icon>}
                </button>
                <div
                    role="region"
                    ref={(el) => (this.#contentContainer = el as HTMLDivElement)}
                    class={{
                        [`${this.#baseClass}__content-container`]: !this.noAccordion
                    }}
                    aria-hidden={!this.isExpanded && !this.noAccordion ? "true" : "false"}
                >
                    <slot />
                </div>
            </Host>
        );
    }
}
